import { useCallback, useMemo, useState } from "react";
import {
  DEFAULT_FILTERS,
  DEFAULT_LEVEL,
  REPORT_COLORS,
  REPORT_ICON_NAMES,
} from "../Constants";

const resolveLocalizedText = (value, isRTL, fallback = "") => {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (value && typeof value === "object") {
    const preferredValue = isRTL ? value.ar ?? value.AR : value.en ?? value.EN;

    if (typeof preferredValue === "string" && preferredValue.trim()) {
      return preferredValue;
    }

    const firstStringValue = Object.values(value).find(
      (item) => typeof item === "string" && item.trim()
    );

    if (firstStringValue) {
      return firstStringValue;
    }
  }

  return fallback;
};

export const useReportState = ({ getIconComponent, isRTL, reportLevels, t }) => {
  const [selectedLevel, setSelectedLevel] = useState(DEFAULT_LEVEL);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [copied, setCopied] = useState(false);
  const [availableReports, setAvailableReports] = useState({
    financialStatements: [],
    financialRatios: [],
    aiAnalysis: [],
  });
  const [reportsLoading, setReportsLoading] = useState(false);

  const getReportList = useMemo(() => {
    if (availableReports.aiAnalysis.length > 0) {
      return availableReports.aiAnalysis.map((report) => report.ReportKey);
    }

    const level = reportLevels.levels.find((item) => item.id === selectedLevel);
    return level ? level.reports : [];
  }, [availableReports.aiAnalysis, reportLevels.levels, selectedLevel]);

  const getAdvancedReportInfo = useCallback(
    (reportKey) => {
      const apiReport = availableReports.aiAnalysis.find(
        (report) => report.ReportKey === reportKey
      );

      if (apiReport) {
        return {
          name: resolveLocalizedText(apiReport.Translation, isRTL, reportKey),
          requiresAdditionalData: false,
          icon: getIconComponent(REPORT_ICON_NAMES[reportKey] || "FaChartArea"),
          color: REPORT_COLORS[reportKey] || "gray",
          fromApi: true,
          allowedModels: apiReport.AllowedModels,
        };
      }

      const levelMap = {
        basic: "basicReports",
        advanced: "advancedReports",
        diagnostic: "diagnosticReports",
      };

      let reportName = resolveLocalizedText(
        t(`reports.${levelMap[selectedLevel]}.${reportKey}`, reportKey),
        isRTL,
        reportKey
      );

      if (isRTL && reportName === reportKey) {
        reportName =
          {
            general: "تقرير مخصص",
            balance_sheet: "الميزانية العمومية",
            income_statement: "قائمة الدخل",
            cash_flow: "قائمة التدفقات النقدية",
            ratios_analysis: "تحليل النسب المالية",
            trend_analysis: "تحليل الاتجاهات",
            comparative_analysis: "تحليل مقارن",
            vertical_analysis: "التحليل الرأسي",
            horizontal_analysis: "التحليل الأفقي",
            custom: "تقرير مخصص",
          }[reportKey] || reportKey;
      }

      return {
        name: reportName,
        requiresAdditionalData: reportKey === "general",
        icon: getIconComponent(REPORT_ICON_NAMES[reportKey] || "FaChartArea"),
        color: REPORT_COLORS[reportKey] || "gray",
        fromApi: false,
      };
    },
    [availableReports.aiAnalysis, getIconComponent, isRTL, selectedLevel, t]
  );

  const handleApplyFilters = useCallback((newFilters) => {
    setFilters((previous) => ({ ...previous, ...newFilters }));
  }, []);

  const handleResetSelection = useCallback(() => {
    setSelectedReport(null);
    setShowReport(false);
    setError(null);
    setCopied(false);
  }, []);

  const handleResetAllFilters = useCallback(
    (toastMessages, toast) => {
      setFilters(DEFAULT_FILTERS);
      toast({
        title: toastMessages.resetSuccess,
        description: toastMessages.resetDesc,
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    },
    []
  );

  return {
    selectedLevel,
    setSelectedLevel,
    selectedReport,
    setSelectedReport,
    showReport,
    setShowReport,
    error,
    setError,
    filters,
    setFilters,
    copied,
    setCopied,
    availableReports,
    setAvailableReports,
    reportsLoading,
    setReportsLoading,
    getReportList,
    getAdvancedReportInfo,
    handleApplyFilters,
    handleResetSelection,
    handleResetAllFilters,
  };
};
